import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { TreetopPropertiesProvider } from 'shared/util-common';

import { AnchoredComment, Comment } from './comment.model';

export enum CommentsFilter {
  None = 0,
  Resolved = 1,
  Unresolved = 2,
}

export type CommentGetOptions = {
  enrichCommentsWithContextInfo?: boolean;
  filter: CommentsFilter;
};

@Injectable({ providedIn: 'root' })
export class CommentHttpService {
  private http = inject(HttpClient);

  private baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api/comments`;

  public getComments(guidelineId: string, version: string, options?: Partial<CommentGetOptions>): Observable<AnchoredComment[]> {
    let params = new HttpParams();

    if (options?.filter !== undefined) {
      params = params.set('filter', options.filter.toString());
    }

    if (options?.enrichCommentsWithContextInfo !== undefined) {
      params = params.set('enrichCommentsWithContextInfo', options.enrichCommentsWithContextInfo.toString());
    }

    return this.http.get<AnchoredComment[]>(`${this.baseUrl}/${guidelineId}/${version}`, {
      params,
    });
  }

  public getOwnFeedback(guidelineId: string, version: string): Observable<AnchoredComment[]> {
    return this.http.get<AnchoredComment[]>(`${this.baseUrl}/feedback/${guidelineId}/${version}`);
  }

  public getOwnPersonalNotes(guidelineId: string, version: string): Observable<AnchoredComment[]> {
    return this.http.get<AnchoredComment[]>(`${this.baseUrl}/personal-notes/${guidelineId}/${version}`);
  }

  public getFeedbackForManage(guidelineId: string, version?: string): Observable<AnchoredComment[]> {
    const versionPath = version ? `/${version}` : '';
    return this.http.get<AnchoredComment[]>(`${this.baseUrl}/feedback/manage/${guidelineId}${versionPath}`);
  }

  public saveComments(guidelineId: string, version: string, comments: AnchoredComment[]): Observable<AnchoredComment[]> {
    return this.http.put<AnchoredComment[]>(`${this.baseUrl}/batch/${guidelineId}/${version}`, comments);
  }

  public addComment(comment: AnchoredComment | Comment, guidelineId: string, version: string): Observable<AnchoredComment | Comment> {
    const isAnchoredComment = 'anchor' in comment;

    return this.http.post<AnchoredComment | Comment>(`${this.baseUrl}/${guidelineId}/${version}`, {
      ...comment,
      guidelineUniqueId: isAnchoredComment ? guidelineId : undefined,
    });
  }

  public addFeedback(comment: AnchoredComment | Comment, guidelineId: string, version: string): Observable<AnchoredComment | Comment> {
    return this.http.post<AnchoredComment | Comment>(`${this.baseUrl}/feedback/${guidelineId}/${version}`, comment);
  }

  public addPersonalNote(comment: AnchoredComment | Comment, guidelineId: string, version: string): Observable<AnchoredComment | Comment> {
    return this.http.post<AnchoredComment | Comment>(`${this.baseUrl}/personal-notes/${guidelineId}/${version}`, comment);
  }

  public editComment(comment: AnchoredComment | Comment): Observable<AnchoredComment | Comment> {
    return this.http.put<AnchoredComment>(`${this.baseUrl}/${comment.id}`, comment);
  }

  public deleteComment(commentId: string): Observable<string> {
    return this.http.delete<void>(`${this.baseUrl}/${commentId}`).pipe(map(() => commentId));
  }
}
